import _extends from '@babel/runtime/helpers/extends';
import React__default, { useRef } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import mergeRefs from 'react-merge-refs';
import Overlay from '../Overlay/Overlay.js';
import useId from '../../hooks/useId.js';
import Text from '../Text/Text.js';
import Button from '../Button/Button.js';
import DialogActions, { BASE_CLASS as BASE_CLASS$2 } from './DialogActions.js';
import DialogContent, { BASE_CLASS as BASE_CLASS$1 } from './DialogContent.js';
import DialogFooter from './DialogFooter.js';

const DIALOG_PROPS = {
  width: ['xxs', 'xs', 'sm', 'md', 'lg', 'xl']
};
const BASE_CLASS = 'nui-dialog';
const Classes = {
  DIALOG: BASE_CLASS,
  CONTAINER: `${BASE_CLASS}-container`,
  HEADER: `${BASE_CLASS}-header`,
  HEADER_TITLE: `${BASE_CLASS}-header-title`
};
const dialogPropTypes = {
  'aria-describedby': PropTypes.string,
  'aria-labelledby': PropTypes.string,
  autoFocus: PropTypes.bool,
  canOutsideClickClose: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  closeButtonProps: PropTypes.shape({}),
  containerClassName: PropTypes.string,
  containerProps: PropTypes.shape({}),
  containerRef: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func]),
  enforceFocus: PropTypes.bool,
  isCloseButtonShown: PropTypes.bool,
  leftHeaderElement: PropTypes.node,
  onClose: PropTypes.func,
  style: PropTypes.shape({}),
  title: PropTypes.node,
  width: PropTypes.oneOfType([PropTypes.oneOf(DIALOG_PROPS.width), PropTypes.number]),
  close: PropTypes.func
};
function Dialog({
  'aria-describedby': ariaDescribedby,
  'aria-labelledby': ariaLabeledby,
  autoFocus = true,
  autoFocusParams = {},
  canOutsideClickClose = false,
  children,
  className,
  closeButtonProps,
  containerClassName,
  containerRef,
  containerProps,
  enforceFocus = true,
  isCloseButtonShown = true,
  leftHeaderElement,
  onClose,
  style,
  title,
  width = 'xs',
  ...rest
}) {
  const titleId = useId();
  const closeButton = useRef(null);
  const maybeRenderCloseButton = () => {
    if (!isCloseButtonShown) return null;
    return /*#__PURE__*/React__default.createElement(Button, _extends({
      "aria-label": "Close",
      onClick: onClose,
      iconProps: {
        name: 'close',
        title: 'Close'
      },
      size: "medium",
      variant: "secondary",
      iconOnly: true
    }, closeButtonProps, {
      ref: closeButtonProps?.ref ? mergeRefs([closeButtonProps.ref, closeButton]) : closeButton
    }));
  };
  const maybeRenderHeader = () => {
    if (title == null) return null;
    return /*#__PURE__*/React__default.createElement("div", {
      className: Classes.HEADER
    }, leftHeaderElement, /*#__PURE__*/React__default.createElement(Text, {
      element: "h5",
      size: "medium",
      id: titleId,
      className: Classes.HEADER_TITLE,
      ellipsized: true
    }, title), maybeRenderCloseButton());
  };
  return /*#__PURE__*/React__default.createElement(Overlay, _extends({
    autoFocus: autoFocus,
    autoFocusParams: {
      findElement: true,
      contentClass: BASE_CLASS$1,
      actionsClass: BASE_CLASS$2,
      fallbackElement: closeButton,
      ...autoFocusParams
    },
    canOutsideClickClose: canOutsideClickClose,
    containerType: "scroll",
    enforceFocus: enforceFocus,
    onClose: onClose,
    hasBackdrop: true
  }, rest), /*#__PURE__*/React__default.createElement("div", {
    className: cx(Classes.CONTAINER, containerClassName),
    ref: containerRef
  }, /*#__PURE__*/React__default.createElement("div", _extends({
    className: cx(Classes.DIALOG, className, typeof width === 'string' && `${BASE_CLASS}--width-${width}`),
    role: "dialog",
    "aria-labelledby": ariaLabeledby || (title ? titleId : undefined),
    "aria-describedby": ariaDescribedby,
    style: {
      ...style,
      width: typeof width !== 'string' ? width : undefined
    }
  }, containerProps), maybeRenderHeader(), children)));
}
Dialog.propTypes = dialogPropTypes;
Dialog.displayName = 'NuiDialog';
var Dialog$1 = Object.assign(Dialog, {
  Actions: DialogActions,
  Content: DialogContent,
  Footer: DialogFooter
});

export { DIALOG_PROPS, Dialog$1 as default };
